<template>
  <v-container fluid>
    <alert-unsaved-changes v-if="unsavedChanges && edit"></alert-unsaved-changes>
    <v-form ref="contactForm">
      <v-row>
        <v-col cols="12">
          <h3 class="secondary300--text">{{ $t('public-contact') }}</h3>
        </v-col>
      </v-row>
      <div v-for="(publicContact, index) in provider.publicContacts" :key="index">
        <v-row class="mt-5" v-if="!publicContact.verified && edit">
          <v-col cols="12" id="verify-email" class="primary400--text pt-4 pb-4">
            <v-row>
              <v-col
                cols="12"
                xl="9"
                lg="9"
                md="9"
                sm="12"
                class="d-flex flex-row justify-left align-center"
              >
                <b>The user has not been verified.</b>
              </v-col>
              <v-col cols="12" xl="3" lg="3" md="3" sm="12" class="d-flex flex-row justify-end">
                <v-btn
                  depressed
                  rounded
                  @click="resendVerification(publicContact)"
                  outlined
                  class="primary200--text font-weight-bold"
                  style="background: white"
                >
                  {{ 'Resend Verification' }}
                </v-btn>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
        <v-row align="center">
          <v-col cols="10" sm="10" md="6">
            <label>Email</label>
            <p class="label-info">
              Email of the Provider's contact person to be displayed at the portal or general email
              to contact Provider.
            </p>
            <v-text-field
              v-model.trim="publicContact.email"
              :placeholder="$t('email')"
              outlined
              type="email"
              class="required"
              :rules="emailRules()"
              clearable
            ></v-text-field>
          </v-col>
          <v-col cols="2" class="mt-5" v-if="provider.publicContacts.length > 1">
            <v-btn fab small text @click="removePublicContact(index)">
              <v-icon>
                $vuetify.icons.trash
              </v-icon>
            </v-btn>
          </v-col>
        </v-row>

        <!-- Optional Contact -->
        <v-row v-if="edit">
          <v-col cols="12">
            <v-expansion-panels>
              <v-expansion-panel>
                <v-expansion-panel-header>
                  {{ $t('optional-info') }}
                </v-expansion-panel-header>
                <v-expansion-panel-content class="mt-5">
                  <v-row>
                    <v-col cols="12" sm="12" md="6">
                      <label>First Name</label>
                      <p class="label-info">
                        First Name of the Provider's contact person to be displayed at the portal
                      </p>
                      <v-text-field
                        v-model.trim="publicContact.firstname"
                        :placeholder="'First Name'"
                        outlined
                        clearable
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="12" md="6">
                      <label>Last Name</label>
                      <p class="label-info">
                        Last Name of the Provider's contact person to be displayed at the portal.
                      </p>
                      <v-text-field
                        v-model.trim="publicContact.lastname"
                        :placeholder="'Last Name'"
                        outlined
                        clearable
                      ></v-text-field>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12" sm="12" md="6">
                      <label>Phone</label>
                      <p class="label-info">
                        Phone of the provider's contact person to be displayed at the portal or
                        general phone to <br />
                        contact Provider.
                      </p>
                      <v-text-field
                        v-model.trim="publicContact.phone"
                        :placeholder="'Phone'"
                        outlined
                        clearable
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="12" md="6">
                      <label>Position</label>
                      <p class="label-info">
                        Position of the Provider's contact person to be displayed at the portal.<br /><br />
                      </p>
                      <v-text-field
                        v-model.trim="publicContact.position"
                        :placeholder="'Position'"
                        outlined
                        clearable
                      ></v-text-field>
                    </v-col>
                  </v-row>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-col>
        </v-row>
      </div>

      <v-row v-if="edit" class="mt-5">
        <v-col cols="4">
          <v-btn
            @click="addPublicContact"
            depressed
            rounded
            large
            color="primary200"
            class="white--text font-weight-bold"
          >
            <v-icon left>
              $vuetify.icons.plus
            </v-icon>
            <span class="text-decoration-underline text-transform-none font-weight-bold">
              Add a Public Contact
            </span>
          </v-btn>
        </v-col>
      </v-row>

      <!-- Buttons -->
      <v-row class="my-5" justify="end">
        <v-btn
          v-if="unsavedChanges && edit"
          @click="cancel"
          outlined
          class="black--text font-weight-bold mx-2"
          rounded
          x-large
        >
          {{ $t('cancel') }}
        </v-btn>
        <v-btn
          v-if="unsavedChanges && edit"
          @click="updatePublicContactInfo"
          depressed
          rounded
          x-large
          color="primary200"
          class="white--text font-weight-bold"
        >
          {{ $t('update') }}
        </v-btn>
        <v-btn
          v-if="!unsavedChanges"
          depressed
          rounded
          x-large
          color="black"
          @click="back"
          outlined
          class="black--text font-weight-bold mx-2"
        >
          <v-icon color="black">$vuetify.icons.back</v-icon>
          {{ $t('back') }}
        </v-btn>
        <v-btn
          v-if="!unsavedChanges"
          depressed
          rounded
          x-large
          color="primary200"
          @click="next"
          class="white--text font-weight-bold"
          >{{ $t('next') }}
          <v-icon color="white">$vuetify.icons.next</v-icon>
        </v-btn>
      </v-row>
    </v-form>
  </v-container>
</template>

<script>
import util from '@/mixins/util.js';
import AlertUnsavedChanges from '@/components/shared/helpers/AlertUnsavedChanges';
import EventBus from '@/helpers/event-bus.js';

export default {
  name: 'AddProviderContactInfo',
  components: { AlertUnsavedChanges },
  mixins: [util],
  data: () => ({
    provider: {
      publicContacts: [
        {
          verified: false,
          email: '',
          id: null,
          firstname: '',
          lastname: '',
          phone: '',
          position: '',
        },
      ],
    },
    unsavedChanges: false,
    dataLoaded: false,
    initialProvider: {},
  }),
  props: {
    edit: {
      type: Boolean,
      default: false,
    },
  },
  mounted() {
    if (this.edit) {
      this.getProvider();
    }
  },
  watch: {
    'provider.publicContacts': {
      deep: true,
      handler(newPublicContacts) {
        if (!this.dataLoaded) return;

        if (JSON.stringify(newPublicContacts) !== JSON.stringify(this.initialProvider)) {
          this.unsavedChanges = true;
        } else {
          this.unsavedChanges = false;
        }
      },
    },
  },
  methods: {
    back() {
      this.$emit('tab', 2);
    },
    next() {
      this.isFormValid = this.$refs.contactForm.validate();
      if (this.isFormValid) {
        const email = this.provider.publicContacts.map(contact => ({
          email: contact.email,
        }));

        this.$emit('tab', 4);
        this.$emit('providerContactInfo', { publicContacts: email });
      }
    },
    getProvider() {
      EventBus.$emit('waiting', true);
      const id = this.$route.params.id;

      this.axios
        .get('/api/v1/provider-settings/' + id)
        .then(response => {
          console.log('Get Provider Contact Info', response.data);

          if (response.data.publicContacts) {
            this.provider.publicContacts = response.data.publicContacts.map(contact => ({
              email: contact.email,
              id: contact.id,
              firstname: contact.firstname,
              lastname: contact.lastname,
              phone: contact.phone,
              position: contact.position,
              verified: contact.verified,
            }));

            this.initialProvider = JSON.parse(JSON.stringify(this.provider.publicContacts));
          }

          this.dataLoaded = true;
        })
        .catch(error => {
          console.error('Error fetching provider data:', error);
        })
        .finally(() => {
          EventBus.$emit('waiting', false);
        });
    },
    addPublicContact() {
      this.provider.publicContacts.push({
        email: '',
        id: null,
        firstname: '',
        lastname: '',
        phone: '',
        position: '',
      });
    },
    removePublicContact(index) {
      this.provider.publicContacts.splice(index, 1);
    },
    cancel() {
      this.getProvider();
    },
    updatePublicContactInfo() {
      const id = this.$route.params.id;
      const providerUpdateData = {
        id: id,
        publicContacts: [...this.provider.publicContacts],
      };

      this.deleteEmptyProperties(providerUpdateData);

      this.isFormValid = this.$refs.contactForm.validate();
      if (this.isFormValid) {
        this.axios
          .post('/api/v1/provider-settings/contact-info', providerUpdateData)
          .then(response => {
            console.log('Provider info updated successfully:', response.data);
            EventBus.$emit('snackbar', {
              text: 'Equipment Updated Successfully',
              color: 'primary200',
            });

            this.unsavedChanges = false;
            this.$emit('updatedChanges', true);
          })
          .catch(error => {
            console.error('Error updating provider info:', error);
          });
      }
    },
    deleteEmptyProperties(obj) {
      if (!obj || typeof obj !== 'object') {
        return;
      }

      if (Array.isArray(obj)) {
        obj.forEach((item, index) => {
          if (item === null || item === undefined || item === '') {
            obj.splice(index, 1);
          } else if (typeof item === 'object') {
            this.deleteEmptyProperties(item);
            if (Object.keys(item).length === 0) {
              obj.splice(index, 1);
            }
          }
        });
      } else {
        for (const key in obj) {
          if (obj[key] === null || obj[key] === undefined || obj[key] === '') {
            delete obj[key];
          } else if (Array.isArray(obj[key])) {
            this.deleteEmptyProperties(obj[key]);
            if (obj[key].length === 0) {
              delete obj[key];
            }
          } else if (typeof obj[key] === 'object') {
            this.deleteEmptyProperties(obj[key]);
            if (Object.keys(obj[key]).length === 0) {
              delete obj[key];
            }
          }
        }
      }
    },
    resendVerification(publicContact) {
      const id = publicContact.id;

      EventBus.$emit('waiting', true);
      this.axios
        .post('/api/v1/provider-settings/resend-verification', { id })
        .then(() => {
          console.log('Success');
          EventBus.$emit('snackbar', {
            text: 'Verification sent it successfully',
            color: 'primary200',
          });
        })
        .catch(error => {
          console.log({ error });
        })
        .finally(() => {
          EventBus.$emit('waiting', false);
        });
    },
  },
};
</script>
